import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInvoiceStore } from "../../../state";
import { useInvoiceSearch } from "../../../hooks";
import ExpenseEmptyIcon from "../../../assets/icons/InvCusExpEmptyIcon";
import EmptyPage from "../../../components/emptyPage/EmptyPage";
import { useInvoiceStatsData } from "../../../hooks/Stats/invoice";
import { servicesType } from "../../../constants/Subscription";
import ActionModal from "../../../components/common/ActionModal";
import UploadIcon from "../../../assets/icons/svgicons/UploadIcon.svg";
import CreateInvoiceIcon from "../../../assets/icons/svgicons/createInvoice.svg";
import { SubscriptionModal } from "../../../components/common/Subscription/SubscriptionModal";
import { MobileSubscriptionModal } from "../../../components/common/Subscription/MobileSubscriptionModal";
import { defaultShortcuts } from "../../../constants/helpServices";
import DatePickerWithShortcuts from "../../../components/DatePicker";
import { ICustomer } from "../../../@types";
import { useTranslation } from "react-i18next";
import useRetailBusinessStatus from "../../../hooks/invoice/useRetailBusinessStatus";
import generateActionButtons from "../../../components/Sales/invoice/invoiceCreationModal";
import { ListInfoCardProps } from "../../../components/list-page/desktop/ListInfoCard";
import ReusableListPage from "../../../components/list-page";
import {
  invoiceCategories,
  invoiceRecurringCategories,
  mobileInvoiceCategories,
  recurringCategories,
  recurringInvoiceCategories,
} from "../../../constants/filterCategories";
import PageLoader from "../../../components/common/PageLoader";
import { useRecurringInvoiceStatsData } from "../../../hooks/Stats/recurringInvoice";
import { ExpiredIcon } from "../../../assets/icons/expiredIcon";
import { CancelIcon } from "../../../assets/icons/CancelIcon";
import { PauseIcon } from "../../../assets/icons/PauseIcon";
import { NumberIcon } from "../../../assets/icons";
import { useInvoiceReoccurringStore } from "../../../state/useInvoiceRecurringStore";
import {
  columns,
  renderInvoicesData,
} from "../../../utils/recurring-invoice-list";
import dayjs from "dayjs";

const ListRecurringInvoicePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentDate = new Date();
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });
  const [startOfMonth, setStartOfMonth] = useState(
    dayjs().subtract(30, "days").startOf("day").toDate(),
  );
  const [endOfMonth, setEndOfMonth] = useState(dayjs().endOf("day").toDate());
  const { recurringData, error } = useRecurringInvoiceStatsData(
    startOfMonth,
    endOfMonth,
  );
  const { t } = useTranslation("invoices");

  const {
    fetchRecurringInvoices,
    fetchMoreData,
    setSearchText: onSearchData,
    setFilter: handleFilter,
    fetchData,
    invoices,
    isLoading,
    range,
    pagination,
    searchText,
    filter,
    isLoadingFilter,
    isLoadingInitial,
    isLoadingMore,
    clearAllFilters,
    hasData,
  } = useInvoiceReoccurringStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] =
    React.useState(false);
  const [subscriptionErrorMessage, setSubscriptionErrorMessage] =
    React.useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const isRetailBusiness = useRetailBusinessStatus();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>("Last 30 Days");
  const [selected, setSelected] = useState<[Date, Date] | null>(null);
  const {
    data: searchResult,
    isSearching,
    searchedDone,
    setSearchedDone,
    handleSearch: handleInvoiceSearch,
  } = useInvoiceSearch();

  useEffect(() => {
    if ("reoccurringStatus" in filter) {
      fetchData({ actionType: "filter" });
    }
  }, [searchText, filter, fetchData]);

  useEffect(() => {
    if (dateRange.startDate || dateRange.endDate) {
      fetchData({ startDate: dateRange.startDate, endDate: dateRange.endDate });
    }
  }, [range, dateRange, fetchData]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const startDateRange = params.get("startDate") || "";
    const endDateRange = params.get("endDate") || "";
    setDateRange({ startDate: startDateRange, endDate: endDateRange });
  }, [location.search]);

  const fetchInvoicePage = () => {
    setIsModalOpen(true);
  };

  const handleInventoryInvoiceClick = async () => {
    navigate("/invoices/create");
    return;
  };
  const handleRecurringInvoiceClick = async () => {
    navigate("/invoices/recurringList");
  };

  const handleFreeformInvoiceClick = () => {
    navigate("/invoices/createFreeform");
  };

  const handleCalendarToggle = () => {
    setShowCalendar(!showCalendar);
  };

  const infoCardData = [
    {
      title: "Active Invoice",
      mobileTitle: "Active Invoice",
      value: `${recurringData?.totalActiveReoccurring || 0}`,
      bgColor: "#ECFDF3",
      className: "w-full",
    },
    {
      title: "Paused Invoice",
      mobileTitle: "Paused Invoice",
      value: `${recurringData?.totalPausedReoccurring || 0}`,
      bgColor: "#F0F7FF",
      className: "w-full",
    },
    {
      title: "Cancelled Invoice",
      mobileTitle: "Cancelled Invoice",
      value: `${recurringData?.totalCancelledReoccurring || 0}`,
      bgColor: "#FFFAEB",
      className: "w-full",
    },
    {
      title: "Expired Invoice",
      mobileTitle: "Expired Invoice",
      value: `${recurringData?.totalExpiredReoccurring || 0}`,
      bgColor: "#FEF3F2",
      className: "w-full",
    },
  ] as ListInfoCardProps[];

  const handleRangeSelect = async (ranges: any) => {
    setShowCalendar(false);
    setSelected(ranges);
    const startDate = ranges[0];
    const endDate = ranges[1];
    let label = "";
    for (const shortcut of defaultShortcuts) {
      const [shortcutStart, shortcutEnd] = shortcut.range();

      if (
        startDate.getTime() === shortcutStart.getTime() &&
        endDate.getTime() === shortcutEnd.getTime()
      ) {
        label = shortcut.label;
        break;
      }
    }
    if (label) {
      setSelectedLabel(label);
    } else {
      setSelectedLabel(
        `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
      );
    }
    setStartOfMonth(new Date(startDate));
    setEndOfMonth(new Date(endDate));
  };

  const handleApplyButtonClick = async (
    startDate: Date,
    endDate: Date | null,
  ) => {
    if (endDate === null) {
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
    }
    setEndDate(startDate);
    setStartDate(endDate);
    const newStartDate = startDate?.toISOString().replace(".000Z", "");
    const newEndDate = endDate?.toISOString().replace(".000Z", "");
    setDateRange({ startDate: newStartDate, endDate: newEndDate });
    try {
      await handleRangeSelect([startDate, endDate]);
      handleCalendarToggle();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    if (!showCalendar) {
      setSelected(null);
    }
  }, [showCalendar]);

  const handleFilterSubmit = (filter: Record<string, string | boolean>) => {
    if (filter.reoccurringStatus !== undefined) {
      //@ts-ignore
      const newFilter: Partial<Filter> = {};
      if (filter.reoccurringStatus !== undefined) {
        newFilter.reoccurringStatus = filter.reoccurringStatus as string;
      }
      //@ts-ignore
      handleFilter(newFilter as Filter);
    }
  };

  const actionButtons = generateActionButtons(
    isRetailBusiness,
    handleInventoryInvoiceClick,
    handleFreeformInvoiceClick,
  );

  const clearAllFilter = () => {
    setDateRange({ startDate: "", endDate: "" });
    setStartDate(null);
    setEndDate(null);
    onSearchData("");
    handleFilter({ reoccurringStatus: "" });
    setSelectedLabel("Last 30 Days");
    setStartOfMonth(dayjs().subtract(30, "days").startOf("day").toDate());
    setEndOfMonth(dayjs().endOf("day").toDate());
    fetchData();
  };

  if (isLoadingInitial) {
    return (
      <>
        <PageLoader />
      </>
    );
  }

  if (error) {
    return (
      <div>
        {typeof error === "object" && "message" in error && error.message
          ? error.message
          : JSON.stringify(error)}
      </div>
    );
  }
  return (
    <>
      <div>
        <ReusableListPage
          categories={recurringInvoiceCategories}
          mobileCategories={recurringCategories}
          hasData={hasData}
          createTitle="Create Invoice"
          pageTitle="Recurring Invoice"
          showReOccurring={false}
          reOccurringText="Recurring invoice"
          showCalendar={showCalendar}
          infoCardData={infoCardData}
          handleCalendarToggle={handleCalendarToggle}
          handleCreate={fetchInvoicePage}
          selectedLabel={selectedLabel}
          fetchMoreData={fetchMoreData}
          onSearchData={onSearchData}
          handleFilter={handleFilter}
          handleSearch={handleInvoiceSearch}
          meta={pagination}
          data={renderInvoicesData(invoices, true)}
          modules="recurring-invoices"
          searchDisplayKey="customer.firstName"
          searchPlaceHolder="Search by invoice number or customer name"
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          isLoadingFilter={isLoadingFilter}
          isSearching={isSearching}
          searchResult={searchResult}
          selectedFilter={filter}
          searchedDone={searchedDone}
          setSearchedDone={setSearchedDone}
          columns={columns}
          onSubmit={handleFilterSubmit}
          mobileCard={"invoiceCard"}
          clearAllFilters={clearAllFilters}
          emptyStateDescription={t("invoice.invoiceFirst")}
          emptyPageIcon={<ExpenseEmptyIcon />}
          emptyPageHref="https://www.youtube.com/embed/fJUWHHoUaxo"
          emptyPageLinkName="invoices"
          handleSetCustomer={function (
            selectedCustomer: ICustomer | null,
          ): void {
            throw new Error("Function not implemented.");
          }}
          emptyMessage={t("invoice.emptyState")}
        />
      </div>

      <div ref={containerRef}>
        <ActionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Select an invoice creation option"
          icon={CreateInvoiceIcon}
          actionButtons={actionButtons}
        />
      </div>

      <div className="mt-[-30em]">
        {showSubscriptionModal && (
          <SubscriptionModal>
            <MobileSubscriptionModal
              onClose={() => setShowSubscriptionModal(false)}
              titleText={`Need to access more features?`}
              subtitleText={subscriptionErrorMessage}
            />
          </SubscriptionModal>
        )}
      </div>

      {showCalendar && (
        <>
          <div className="bg-gray-900 mt-1 bg-opacity-50 fixed top-0 right-0 left-0 z-50 h-[100vh]">
            <div className="hidden md:flex justify-center items-center h-full w-full">
              <DatePickerWithShortcuts
                onRangeSelect={handleRangeSelect}
                selected={selected}
                onClose={handleCalendarToggle}
                onApplyButtonClick={(startDate, endDate) =>
                  handleApplyButtonClick(startDate, endDate)
                }
                endDate={endDate ?? endOfMonth}
                startDate={startDate ?? startOfMonth}
                fetchData={clearAllFilter}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export { ListRecurringInvoicePage };
